import dayjs from 'dayjs';

import { TokenResponse } from '#/client/models';
import { StorageKeys } from '#/service/constants';
import { LS } from '#/service/storage';
import { ignoreError } from '#/service/utils';

import { authActions, authStore } from '../auth/auth.store';
import { snackbarActions } from './snackbar.store';

/**
 * Checks if the last shown greeting interval has passed
 * @param interval ISO that contains current time - minimum greeting interval set in Admin.
 * @returns true if the last shown greeting date is before the provided interval, false otherwise.
 */
export const hasGreetingIntervalPassed = (interval: string) => {
  const lastGreetingTimestamp = LS.get<string>(
    StorageKeys.PERSONAL_GREETING_LAST_SHOWN
  );

  // if there is no last shown date stored or invalid format, we consider the interval has passed.
  if (!lastGreetingTimestamp || !dayjs(lastGreetingTimestamp).isValid()) {
    return true;
  }

  // if the last shown date is before the provided interval, we show the greeting.
  return dayjs(lastGreetingTimestamp).isBefore(interval);
};

const displayGreeting = (message: string) => {
  LS.set(StorageKeys.PERSONAL_GREETING_LAST_SHOWN, dayjs().toISOString());
  snackbarActions.showSnackbar({ message });
};

/** Shows a personal greeting snackbar based on the provided token response. */
export const showPersonalGreeting = ({ greeting }: TokenResponse) => {
  // return early if the greeting feature is not enabled or user is not logged in.
  if (!greeting || !authStore.isLoggedIn) {
    return;
  }

  const { show, message } = greeting;

  const shouldShow =
    show === 'ALWAYS' ||
    (show === 'IF_LAST_SHOWN_BEFORE' &&
      hasGreetingIntervalPassed(greeting.ifShownBefore));

  if (!shouldShow) {
    return;
  }

  if (show === 'ALWAYS') {
    displayGreeting(message);

    return;
  }

  authActions
    .refreshProfile()
    .then((res) => {
      if (!res.userId) {
        return;
      }

      displayGreeting(message);
    })
    .catch(ignoreError);
};
